import { fetchAuthSession } from 'aws-amplify/auth';

export type ProfileRecord = {
  profileName: string;
  description?: string;
  values?: Record<string, string>;
  status: 'active' | 'archived' | 'draft';
  createdAt: string;
  updatedAt: string;
  createdBy?: string;
  version?: number;
};

const getAuthHeaders = async () => {
  const session = await fetchAuthSession();
  const idToken = session.tokens?.idToken?.toString();
  if (!idToken) throw new Error('Not authenticated');
  return {
    'Content-Type': 'application/json',
    'Authorization': `Bearer ${idToken}`
  } as const;
};

const baseUrl = () =>
  process.env.NODE_ENV === 'development'
    ? '/Prod'
    : (process.env.REACT_APP_API_BASE_URL || 'https://p811zb8knk.execute-api.us-east-1.amazonaws.com/Prod');

export const profileService = {
  async list(status?: 'active' | 'archived' | 'draft'): Promise<{ profiles: ProfileRecord[]; count: number }> {
    const qs = status ? `?status=${encodeURIComponent(status)}` : '';
    const resp = await fetch(`${baseUrl()}/secure/tally-profiles${qs}`, { headers: await getAuthHeaders() });
    if (!resp.ok) throw new Error(`Failed to list profiles (${resp.status})`);
    return await resp.json();
  },
  async get(profileName: string): Promise<ProfileRecord> {
    const resp = await fetch(`${baseUrl()}/secure/tally-profiles/${encodeURIComponent(profileName)}`, { headers: await getAuthHeaders() });
    if (!resp.ok) throw new Error(`Failed to get profile (${resp.status})`);
    return await resp.json();
  },
  async create(payload: Omit<ProfileRecord, 'createdAt' | 'updatedAt' | 'version'> & { createdBy?: string }): Promise<ProfileRecord> {
    const resp = await fetch(`${baseUrl()}/secure/tally-profiles`, {
      method: 'POST',
      headers: await getAuthHeaders(),
      body: JSON.stringify(payload)
    });
    if (!resp.ok) {
      const text = await resp.text();
      throw new Error(`Failed to create profile: ${resp.status} ${text}`);
    }
    return await resp.json();
  },
  async update(profileName: string, payload: Partial<ProfileRecord>): Promise<ProfileRecord> {
    const resp = await fetch(`${baseUrl()}/secure/tally-profiles/${encodeURIComponent(profileName)}`, {
      method: 'PUT',
      headers: await getAuthHeaders(),
      body: JSON.stringify(payload)
    });
    if (!resp.ok) {
      const text = await resp.text();
      throw new Error(`Failed to update profile: ${resp.status} ${text}`);
    }
    return await resp.json();
  },
  async archive(profileName: string): Promise<{ message: string }> {
    const resp = await fetch(`${baseUrl()}/secure/tally-profiles/${encodeURIComponent(profileName)}`, {
      method: 'DELETE',
      headers: await getAuthHeaders()
    });
    if (!resp.ok) {
      const text = await resp.text();
      throw new Error(`Failed to archive profile: ${resp.status} ${text}`);
    }
    return await resp.json();
  }
};


